import { useInfiniteQuery, InfiniteData } from 'react-query';
import message from 'antd/es/message';

import { request, useCurrentUserId, queryClient } from '@/utils';

import { ProposalItem } from '../types';
import { transProposalDetail } from './detail';

export const PAGE_LIMIT = 50;

export const useProposalList = (
  search: string,
  orderBy: string,
  isMine: boolean,
  isMobile?: boolean,
) => {
  const url = isMine ? '/proposal/related_list' : '/proposal/list';
  const userId = useCurrentUserId();

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    [url, orderBy, isMine, userId, search],
    async ({ pageParam = 0 }) => {
      return request.get<{ proposals: ProposalItem[]; total_count: number }>(url, {
        q: search,
        start: pageParam,
        limit: PAGE_LIMIT,
        order_by: orderBy,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const total = (pages.length - 1) * PAGE_LIMIT + (lastPage?.proposals?.length || 0);

        if (total >= lastPage?.total_count) {
          return false;
        }
        return total;
      },
      select,
      enabled: !isMine || (!isMobile && !!userId),
      onError() {
        message.error('failed to fetch');
      },
    },
  );

  return {
    data: data?.pages,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};

export const invalidProposalList = (isMine?: boolean) => {
  queryClient.invalidateQueries([isMine ? '/proposal/related_list' : '/proposal/list']);
};

const select = (data: InfiniteData<{ proposals: ProposalItem[] }>) => {
  const result = data.pages.map(groupPage);

  return {
    ...data,
    pages: result,
  };
};

const groupPage = (data: { proposals: ProposalItem[] }) => {
  data.proposals?.forEach(transProposalDetail);

  return data.proposals || [];
};
